<script setup lang="ts">
import type { ShopperPromotionsTypes } from 'commerce-sdk-isomorphic'

type StandardPromotionProps = {
  promotion: ShopperPromotionsTypes.Promotion
}
const props = defineProps<StandardPromotionProps>()
const title = computed(() => props.promotion?.c_promotionTitle ?? props.promotion?.calloutMsg)
const showDetails = ref(false)

const parentLocation = inject('location')

const handleClickPromotionAction = (e: MouseEvent, performAction?: (id: string) => void) => {
  const actionEl = promotionDetails.value?.querySelector('[id]')
  if (actionEl && performAction) {
    e.preventDefault()
    performAction(actionEl.id)
  }
}

const promotionDetails = ref<HTMLElement>()
</script>

<template>
  <div class="pdp-promotion">
    <div class="pdp-promotion__title">
      <Markdown class="pdp-promotion__promotion" :content="title" />
      <button
        v-if="promotion.details && parentLocation !== LOCATIONS.addToBagPopup && parentLocation !== LOCATIONS.header"
        class="pdp-promotion__details-button"
        type="button"
        :class="{ 'pdp-promotion__details-button--open': showDetails }"
        :aria-expanded="showDetails"
        :aria-label="
          showDetails
            ? $t('product_details_page.standard_promotion.less_details')
            : $t('product_details_page.standard_promotion.more_details')
        "
        @click="showDetails = !showDetails"
        @keyup.enter="showDetails = !showDetails"
      >
        <svg-icon name="mhj:keyboard-arrow-down" />
      </button>
    </div>

    <Transition name="expand">
      <div class="pdp-promotion__details-expand" v-if="showDetails">
        <div ref="promotionDetails" class="pdp-promotion__details-inner">
          <Action :sfcc-promotion="true" v-slot="action: any">
            <Markdown
              v-if="promotion.details"
              class="pdp-promotion__details"
              :content="promotion.details"
              @click="(e: MouseEvent) => handleClickPromotionAction(e, action.performAction)"
            />
          </Action>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="postcss">
.pdp-promotion {
  font-size: var(--text-small);
  p {
    margin-bottom: 0;
  }
  .pdp-promotion__title {
    display: flex;
    align-items: end;
    gap: var(--space-20);
  }

  .pdp-promotion__promotion {
    color: var(--text-sale);
    text-transform: uppercase;
  }

  .pdp-promotion__details-button {
    display: flex;
    align-items: center;
    padding: var(--space-20);
    &:hover {
      cursor: pointer;
    }
    .icons {
      aspect-ratio: 8 / 5;
      width: 0.5rem;
      height: auto;
      transition: transform 0.2s ease-in-out;
    }
    &.pdp-promotion__details-button--open .icons {
      transform: rotate(180deg);
    }
  }

  .pdp-promotion__details-expand {
    display: grid;
    grid-template-rows: 1fr;
  }

  .pdp-promotion__details-inner {
    min-height: 0;
    overflow: hidden;
  }

  .pdp-promotion__details {
    margin-top: var(--space-30);
    a {
      text-decoration: underline;
      cursor: pointer;
    }
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: grid-template-rows 0.8s ease-in-out;
}

.expand-enter-from,
.expand-leave-to {
  grid-template-rows: 0fr;
}
</style>
